/* -----------------------------------------------------------------------------------------------------
  - Component Name: ScheduleTable
  - Created On: [30-01-2025] by [SREERAJ]
  - Last Modified: [30-10-2025] by [SREERAJ]
  - Description:
      This component displays the Schedule Management Table where admin/supervisor 
      can view, filter, and manage schedules for various worksites.
*/
import React, { useEffect, useState } from 'react'
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal'
import TaskSearchFilter from '../ScheduleSearchFilter/ScheduleSearchFilter'
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable'
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination'
import ScheduleManagementForm from '../ScheduleManagementForm/ScheduleManagementForm'
import { get_schedule_by_id, get_schedule_history_with_out_pagination, get_schedule_with_pagination, query_replay_by_query_id, query_submit_by_id, query_view_by_id, schedule_cancel_by_id, schedule_creation, schedule_mobilize_by_id, schedule_update } from '../../../../config/Worksite/scheduleApis'
import { handlePaginationEntries } from '../../../../utils/pagination'
import { ErrorToast, SuccessToast } from '../../../../utils/toast'
import { useDispatch, useSelector } from 'react-redux'
import { hasPrivilege } from '../../../../utils/privileges'
import Loader from '../../../../Components/Loader/Loader'
import { getUserData } from '../../../../utils/localStorage'
import CustomConfirmModal from '../../../../Components/custom_components/CustomConfirmModal/CustomConfirmModal'
import AddQueryToSchedule from '../Components/AddQueryToSchedule/AddQueryToSchedule'
import { Icons } from '../../../../utils/GlobalIcons'
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory'
import { useNavigate } from 'react-router-dom'
import MobilizeStartConfirmationView from '../Components/MobilizeStartConfirmationView/MobilizeStartConfirmationView'
import './ScheduleTableStyle.css'
import { getActionColors, getStatusColors } from '../../../../utils/constants'
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder'
import ViewQueryModal from '../Components/ViewQuerySchedule/ViewQueryModal'
import CustomLegend from '../../../../Components/custom_components/CustomLegend/CustomLegend'
import { fetchHistoryData } from '../../../../config/HistoryService/historyService'

function ScheduleTable() {
    const navigate = useNavigate()
    const privileges = useSelector((state) => state.privileges.privileges);

    const scheduleFilters = useSelector((state) => state.scheduleFilters.scheduleFilters);

    const userData = getUserData();

    // State variables for managing schedule data, pagination, and modal states
    const [scheduleListing, setScheduleListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });

    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [openQueryModal, setOpenQueryModal] = useState({
        isOpen: false,
        id: '',
    });
    const [viewQueryModal, setViewQueryModal] = useState({
        isOpen: false,
        info: {},
    });
    const [mobilizeModal, setMobilizeModal] = useState({
        isOpen: false,
        id: null,
        Values: null,
    });

    const [cancelSchedule, setCancelSchedule] = useState({
        isOpen: false,
        id: null,
        values: null,
    });

    const [showHistory, setShowHistory] = useState(false); // history modal state
    const [historyData, setHistoryData] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false); // Loader state


    // Fetch schedule data on page change or refresh
    useEffect(() => {
        fetchSchedules();
    }, [pageNumber, totalItemPerPage, scheduleFilters, refresh]);

    // Fetch schedule data from API
    const fetchSchedules = async () => {
        const formData = new FormData();
        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        Object.keys(scheduleFilters).forEach(key => formData.append(key, scheduleFilters[key]));

        try {
            setIsLoading(true)
            const response = await get_schedule_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                setScheduleListing(data.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setScheduleListing([]);
            }
        } catch (error) {

        } finally {
            setIsLoading(false)
        }
    };


    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
        Cancel: 17,
        Mobilize: 18,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.add),
        cancelSchedule: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.Cancel),
        MobilizeSchedule: hasPrivilege(privileges, "Work site", 3, PERMISSION_ACTIONS.Mobilize),
    };

    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;

    //  if TRUE "Cancelled" filter is applied
    const isCancelledFilterApplied = scheduleFilters?.Iscancel === 1;

    // Columns for the table
    const columns = [
        { title: "#", key: "key" },
        { title: "Schedule Code", key: "SD_ScheduleCode" },
        { title: "Project", key: "project" },
        { title: "Main site", key: "mainSite" },
        { title: "Worksite", key: "WD_WorksiteName" },
        { title: "Technology", key: "SD_Technology" },
        { title: "Survey from", key: "SD_Survey_from_date" },
        { title: "Survey to", key: "SD_Survey_end_date" },

        {
            title: "Current Progress",
            key: "CurrentProgress",
            render: (text, record) => (
                <span style={{ color: getActionColors(record.Shedule_process_status).color }}>
                    {isCancelledFilterApplied == 1 ? "" : record.CurrentProgress}
                </span>
            ),
        },
        {
            title: "Status",
            key: "SD_Status",
            render: (text, record) => {
                const style = getStatusColors(record.Status_Id);
                return (

                    <span className="status-badge" style={style}>
                        {record?.SD_Status}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ] //  Filter out “Current Progress and Status when cancelled filter applied || removes the "Actions" column when hasActionPermissions is false
        .filter(
            (column) =>
                (column.key !== "CurrentProgress" || !isCancelledFilterApplied == 1) &&
                (column.key !== "SD_Status" || !isCancelledFilterApplied == 1) &&
                (column.key !== "actions" || hasActionPermissions)
        );

    // Render action icons for each schedule item
    const renderActions = (row) => (

        <div className="action-container">
            {/* is_Schedule_Cancled =0 MEANS SCHEDULE NOT CANCELED */}
            {userPermissions?.view && (
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => ScheduleViewNavigate(row?.id)} />
                </CustomTooltipWithoutRoundBorder>
            )}
            {/* CANCELD  AND HAVE EDIT PRIVILEGE */}
            {row?.is_Schedule_Cancled == 0 && userPermissions?.edit && Number(row?.Shedule_process_status) !== 2 && ( //2MEANS COMPLETED
                <CustomTooltipWithoutRoundBorder title="Edit ">
                    <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>
            )}
            {/* userPermissions?.MobilizeSchedule && */}
            {userPermissions?.MobilizeSchedule && row?.is_Schedule_Cancled == 0 && Number(row?.is_mobilize) === 0 && Number(row?.Status_Id) === 1 && (
                <CustomTooltipWithoutRoundBorder title="Mobilize">
                    <Icons.Mobilize
                        className="mobilize-action-icon"
                        onClick={() => handleMobilizeModal(true, row?.id, row)}
                    />
                </CustomTooltipWithoutRoundBorder>
            )}
            {(row?.Is_query_sended && row?.Is_query_assigned_by) ? (
                <CustomTooltipWithoutRoundBorder title="View Query">
                    <Icons.Comment
                        // QueryReply
                        className="comment-action-icon"
                        onClick={() => handleOpenQueryModal(true, row?.id)}
                    />
                </CustomTooltipWithoutRoundBorder>
            ) : userData?.RoleId === 4 && !row?.Is_query_sended ? ( //already sent query then hide the add query icon
                <CustomTooltipWithoutRoundBorder title="Raise Query">
                    <Icons.Query
                        className="comment-action-icon"
                        onClick={() => handleOpenQueryModal(true, row?.id, 'add')}
                    />
                </CustomTooltipWithoutRoundBorder>
            ) : null}


            {Number(row?.Shedule_process_status) !== 2 && row?.is_Schedule_Cancled == 0 && userPermissions.cancelSchedule && ( //HIDE CANCEL ICON WHEN SCHEDULE IS CANCEL
                <CustomTooltipWithoutRoundBorder title="Cancel Schedule">
                    <Icons.Cancel
                        className="cancel-action-icon"
                        onClick={() => openCancelSchedule(row?.id, row)}
                    />
                </CustomTooltipWithoutRoundBorder>
            )}
            {userPermissions.history &&
                <CustomTooltipWithoutRoundBorder title="History">
                    <Icons.History className="history-action-icon" onClick={() => handleHistoryModal(true, row?.id)} />

                </CustomTooltipWithoutRoundBorder>}
        </div>
    );

    // Map the schedule data into table rows
    const rows = () => scheduleListing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.SD_id,
        SD_ScheduleCode: item.SD_ScheduleCode,
        SD_Status: item?.status?.StatusName,
        Status_Id: item?.status?.SL_ID,
        SD_Survey_from_date: item.SD_Survey_from_date,
        SD_Survey_end_date: item?.SD_Survey_end_date,
        SD_Technology: item.technology?.TechnologiesTypeName,
        Is_query_sended: item?.Is_query_sended,
        Is_query_assigned_by: item?.Is_query_assigned_by,
        WD_WorksiteName: item?.worksite?.WD_WorksiteName || '',
        project: item?.projectName || '',
        is_mobilize: item?.is_mobilize || 0,
        mainSite: item?.mainSite,
        CurrentProgress: item?.schedule_StatusName,
        Shedule_process_status: item?.Shedule_process_status,
        is_Schedule_Cancled: item?.is_cancel || 0,
        is_Invoice_Paid: item?.allInvoicesPaid || false,
        is_urgent: item?.is_urgent == 1 ? true : false

    }));


    const ScheduleViewNavigate = (scheduleId) => {
        navigate(`/worksites/schedule/${scheduleId}`)
    }

    // Open Add/Edit/View modal
    const handleModal = async (isOpen, scheduleID, actionType) => {
        if (actionType === 'view' && scheduleID) { //FOR VIEW
            await fetchScheduleDetails(scheduleID,);
            setIsViewModalOpen(true);
        } else if (scheduleID) { //FOR EDIT
            await fetchScheduleDetails(scheduleID);
        } else { //FOR ADD
            setModalProps({
                isOpen,
                id: '',
                formValues: {
                    SD_ScheduleCode: '',
                    SD_Survey_from_date: '',
                    SD_Survey_end_date: '',
                    SD_TechnologyId: '',
                    SD_FrequencyId: '',
                    SD_ScheduleDescription: '',
                    employee_ids: [],
                    equipment_ids: [],
                    SD_Status: 1,
                    SD_WorksiteId: '',
                    is_urgent: 0,
                }
            });
        }

    };

    // Fetch details of a specific schedule item
    const fetchScheduleDetails = async (scheduleID, actionType) => {
        try {
            const response = await get_schedule_by_id({ scheduleID });
            if (response?.status === "success") {
                const data = response?.data;

                let employeeArr = [] //FOR SAVE THE WORKFORCE ID IN A ARRAY
                employeeArr = data?.schedule_employee?.map((item) => item?.SEMP_EmployeeID);

                let equipmentsArr = [] //FOR SAVE THE EQUIPMENT ID IN A ARRAY
                equipmentsArr = data?.schedule_equipment?.map((item) => item?.SEM_EquipmentID);

                setModalProps({
                    isOpen: actionType === 'view' ? false : true,
                    id: data?.SD_id,
                    formValues: {
                        SD_ScheduleCode: data?.SD_ScheduleCode,
                        SD_TechnologyId: data?.SD_TechnologyId,
                        SD_FrequencyId: data?.SD_FrequencyId,
                        SD_ScheduleDescription: data?.SD_ScheduleDescription,

                        SD_Survey_from_date: data?.SD_Survey_from_date,
                        SD_Survey_end_date: data?.SD_Survey_end_date,

                        SD_WorksiteId: data?.SD_WorksiteId,
                        employee_ids: employeeArr,
                        equipment_ids: equipmentsArr,
                        SD_Status: data?.SD_Status,
                        is_urgent: data?.is_urgent || 0,
                        is_mobilize: data?.is_mobilize || 0,
                    },
                });
            } else {
                ErrorToast(response?.message || 'Something went wrong!');
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        }
    };

    // Handle saving schedule data (add/edit)
    const handleScheduleSave = async (values, scheduleId) => {
        try {
            setIsButtonDisabled(true);
            const apiFunction = scheduleId ? schedule_update : schedule_creation;
            if (scheduleId) values.ScheduleID = scheduleId;

            const response = await apiFunction(values);
            const message = response?.data?.message || 'Something went wrong!';
            if (response?.data?.status === "success") {
                SuccessToast(message);
                closeModal();
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            const errorMessage = error?.data?.errors ? Object.values(error.data.errors)[0][0] : 'Something went wrong!';
            ErrorToast(errorMessage);
            console.log("errorMessage", errorMessage);

        } finally {
            setIsButtonDisabled(false);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setIsViewModalOpen(false);
    };

    // FOR CLSOE THE QUERY MODAL
    const closeQueryModal = () => {
        setOpenQueryModal({ isOpen: false, id: '' });
    };
    // FOR CLSOE THE VIEW QUERY MODAL
    const closeViewQueryModal = () => {
        setViewQueryModal({ isOpen: false, info: '' });
    };

    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    const handleMobilizeModal = (isOpen, id, Values) => {
        setMobilizeModal({
            isOpen: isOpen,
            id,
            Values,
        });
    };

    // Open Cancel Schedule Modal
    const openCancelSchedule = (id, values) => {
        setCancelSchedule({
            isOpen: true,
            id,
            values,
        });
    };

    // Close/Reset Cancel Schedule Modal
    const closeCancelSchedule = () => {
        setCancelSchedule({
            isOpen: false,
            id: null,
            values: null,
        });
    }

    // Function to handle HIDE/SHOW query add MODAL
    const handleOpenQueryModal = (isOpen, id = '', type) => {

        if (type === `add`) { //show add query form
            setOpenQueryModal({ isOpen, id })
        } else { //show the query info
            fetchQueryDetails(id)
        }
    }

    // QUERY SUBMIT
    const handleQuerySubmit = async (values) => {
        try {
            setIsButtonDisabled(true);
            const response = await query_submit_by_id(values);
            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message);
                setOpenQueryModal({ isOpen: false, id: '' });
                setRefresh(!refresh);
            } else {
                ErrorToast(response?.data?.message || "Something went wrong!");
            }
        } catch (error) {
            console.error("Error saving project:", error);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // QUERY REEPLAY SUBMIT
    const handleQueryReplaySubmit = async (values, queryId) => {

        try {
            setIsButtonDisabled(true);
            const response = await query_replay_by_query_id(queryId, {
                reply_text: values.reply_text,
            });
            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message);
                closeViewQueryModal()
                setRefresh(!refresh);
            } else {
                ErrorToast(response?.data?.message || "Something went wrong!");
            }
        } catch (error) {
            console.error("Error saving project:", error);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // Fetch details of a query
    const fetchQueryDetails = async (scheduleID) => {
        try {
            setIsLoading(true)
            const response = await query_view_by_id({ scheduleID });
            if (response?.data?.status === "success") {
                const data = response?.data?.data;
                setViewQueryModal({ isOpen: true, info: data })
            } else {
                ErrorToast(response?.message);
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        } finally {
            setIsLoading(false)
        }
    };

    // FOR HANDLE START SCHEDULE
    const handleStartSchedule = async (scheduleID) => {
        try {
            setIsButtonDisabled(true);
            const response = await schedule_mobilize_by_id({ scheduleID });

            if (response?.status === "success") {
                setRefresh(prev => !prev); // Refresh table
                handleMobilizeModal(false, "", "")
                SuccessToast(response?.message);
            } else {
                ErrorToast(response?.message, true, response?.data);
                // ErrorToast(response?.message || 'Something went wrong!');
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        } finally {
            setIsButtonDisabled(false);
        }
    };


    // FOR HANDLE CANCEL SCHEDULE
    const handleCancelSchedule = async (ScheduleID) => {
        try {
            setIsButtonDisabled(true);
            const params = {
                is_cancel: 1, //(1 is cancel and 0 is defult )
                ScheduleID: ScheduleID
            }
            const response = await schedule_cancel_by_id(params);

            if (response?.status === "success") {
                setRefresh(prev => !prev); // Refresh table
                closeCancelSchedule()
                SuccessToast(response?.message);
            } else {
                ErrorToast(response?.message, true, response?.data);
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        } finally {
            setIsButtonDisabled(false);
        }
    };


    // HISTORY BUTTON CLICK AND API CALL
    const handleHistoryModal = async (open, id) => {
        setShowHistory(open);

        if (open) {
            setHistoryLoader(true);
            try {
                const response = await fetchHistoryData('schedule', id);

                if (response?.status === "success") {

                    //  Transform API response for  table usage
                    const formatted = response?.data?.map((item, index) => ({
                        id: item.id,
                        schedule_name: item.scheduleCode || "-",
                        changedBy: item.changed_by_name || "Unknown",
                        changedAt: item.changed_at || "-",
                        changeDescription: item.change_description || "-",
                    }));

                    setHistoryData(formatted || []);
                } else {
                    // InfoToast("No history data found.");
                    setHistoryData([]);
                }
            } catch (error) {
                console.error("Error fetching project history:", error);
            } finally {
                setHistoryLoader(false);
            }
        } else {
            setHistoryData([]);
        }
    };


    return (
        <>
            <div className='workforce-management-table-container'>
                <TaskSearchFilter
                    tableTitle={`Schedules`}
                    isAddEditModalOpen={modalProps.isOpen}
                    handleAddEditModal={handleModal}
                    isAddPermission={userPermissions?.add}
                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}

                />
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                        <CustomLegend />
                    </div>
                    <CustomTable
                        columns={columns}
                        rows={isLoading ? [] : rows()}
                        renderActions={renderActions}
                        isCheckbox={false}
                        isLoading={isLoading}
                    />
                </div>
                <div className='bottom-container'>
                    {isLoading ? <Loader /> : <CustomBottomPagination
                        items_per_page={totalItemPerPage}
                        current_page={pageNumber}
                        total_page_count={totalPages}
                        on_change_value={handlePageChange}
                        on_page_count_update={handleItemPageCountChange}
                        pagination={pagination}
                    />}
                </div>
            </div>

            {/* Custom Add/Edit/View Modal */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={closeModal}
                title_other=""
                title={isViewModalOpen ? "View Schedule" : modalProps.id ? "Edit Schedule" : "Create Schedule"}
                body={
                    <ScheduleManagementForm
                        isDisabled={isViewModalOpen}
                        isButtonDisabled={isButtonDisabled}
                        id={modalProps.id}
                        formValues={modalProps.formValues}
                        handleSave={handleScheduleSave}
                    />
                }
                maxWidth="800px"
            />}

            {/* Custom Add query Modal */}
            {openQueryModal?.isOpen && <CustomModal
                open={openQueryModal?.isOpen}
                handleClose={closeQueryModal}
                title={"Add Query"}
                body={
                    <AddQueryToSchedule
                        isButtonDisabled={isButtonDisabled}
                        scheduleInfo={openQueryModal}
                        handleSave={handleQuerySubmit}

                    />
                }
                maxWidth="700px"
            />}


            {/* Custom View query Modal */}
            {viewQueryModal?.isOpen && <CustomModal
                open={viewQueryModal?.isOpen}
                handleClose={closeViewQueryModal}
                title={"View Query"}
                body={

                    <ViewQueryModal
                        isButtonDisabled={isButtonDisabled}
                        queryInfo={viewQueryModal?.info}
                        handleSave={handleQueryReplaySubmit}
                    />
                }
                maxWidth="700px"
            />}


            {/* MOBILIZE ACTION MODAL */}
            {mobilizeModal?.isOpen && <CustomModal
                open={mobilizeModal?.isOpen}
                handleClose={() => handleMobilizeModal(false, "")}
                title="Mobilize Schedule"
                body={
                    <MobilizeStartConfirmationView
                        scheduleName={mobilizeModal?.Values?.SD_ScheduleCode || `N/A`}
                        isButtonDisabled={isButtonDisabled}
                        onConfirm={() => handleStartSchedule(mobilizeModal?.id)}
                        onCancel={() => handleMobilizeModal(false, "")}
                    />
                }
                maxWidth="600px"
            />}

            {/* CANCEL SCHEDULE */}
            {cancelSchedule.isOpen &&
                <CustomConfirmModal
                    custom_text={`Do you want to cancel the schedule ${cancelSchedule?.values?.SD_ScheduleCode || "N/A"}`}
                    open_modal={cancelSchedule.isOpen}
                    close_modal={closeCancelSchedule}
                    confirm_function={() => handleCancelSchedule(cancelSchedule.id)}
                />}


            {/* HISTORY VIEW MODAL */}
            {showHistory && <CustomModal
                open={showHistory}
                handleClose={() => handleHistoryModal(false)}
                title="Schedule History"
                maxWidth="75%"
                body={
                    historyLoader ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Loader />
                        </div>
                    ) : (
                        <ActionHistory
                            title=""
                            data={historyData}
                            columns={[
                                { key: "schedule_name", label: "Schedule" },
                                { key: "changedBy", label: "Changed By" },
                                { key: "changedAt", label: "Changed At" },
                                { key: "changeDescription", label: "Change Description" },
                            ]}
                            noDataMessage="No change history available."
                        />

                    )
                }
            />}


        </>
    )
}

export default ScheduleTable